<template>
  <div>
    <megabit-base-dialog :show-dialog="show" :width="1024">
      <template v-slot:body>
        <v-sheet v-if="isLoading" class="pa-3" dark>
          <v-skeleton-loader
            class="mx-auto"
            :max-width="1024"
            type="card"
          ></v-skeleton-loader>
        </v-sheet>
        <v-card v-else dark>
          <v-card-title>
            <span class="headline">Parcelamento</span>
          </v-card-title>
          <v-card-text>
            <v-container>
              <v-form ref="formParcelamento" v-model="cmpValidForm">
                <v-row>
                  <v-col cols="4">
                    <v-row dense>
                      <v-col cols="12">
                        <v-autocomplete
                          :items="tipoVendaList"
                          :value="tabParcelamento.tipoVenda"
                          item-text="DESCRICAO"
                          item-value="CODIGO"
                          label="Condição de Pagamento"
                          class="text-caption"
                          placeholder="Selecione uma Condição de Pagamento"
                          hide-no-data
                          clearable
                          dark
                          dense
                          outlined
                          return-object
                          prepend-inner-icon="mdi-text-box-search"
                          @change="updateParcelField"
                          @keyup="searchParcels($event, 'tipoVenda')"
                          @click:prepend-inner.prevent="openDialog()"
                          :disabled="!enablePaymentConditionField"
                        />
                      </v-col>
                    </v-row>
                    <v-row dense>
                      <v-col>
                        <v-text-field
                          v-model="tabParcelamento.condicao"
                          label="Condição"
                          placeholder="Condição"
                          class="text-caption"
                          dense
                          outlined
                          :maxlength="100"
                          @input="generateParcels"
                          :disabled="!enablePaymentConditionField"
                        />
                      </v-col>
                      <v-col>
                        <v-select
                          v-model="tabParcelamento.ct"
                          :items="parcelCTsList"
                          class="text-caption"
                          label="CTs"
                          persistent-hint
                          dense
                          dark
                          outlined
                          @input="updateCTsOnParcels"
                          :disabled="!enablePaymentConditionField"
                        />
                      </v-col>
                    </v-row>
                    <v-row dense>
                      <v-col v-if="enableAccountField">
                        <c-select
                          item-text="DESCRICAO"
                          item-value="CODIGO"
                          :lista="planoContaList"
                          rotulo="Conta"
                          :item.sync="tabParcelamento.conta"
                          v-model="tabParcelamento.conta"
                          @searchinput="searchParcels($event, 'planoConta')"
                          @input="updateAccountOnParcel"
                        />
                      </v-col>
                    </v-row>
                    <v-row dense>
                      <v-col>
                        <c-input-money
                          v-model="tabParcelamento.valorTotal"
                          defaultClass="d-flex justify-end"
                          label="Valor"
                          dark
                          dense
                          outlined
                          reverse
                          readonly
                          v-bind:valueWhenIsEmpty="'0'"
                          v-bind:options="{
                            locale: 'pt-BR',
                            prefix: '',
                            suffix: '',
                            length: 11,
                            precision: getPrecision(),
                          }"
                        />
                        <div
                          v-if="
                            totalParcelsList - tabParcelamento.valorTotal != 0
                          "
                        >
                          <span class="warning--text"
                            >Diferença
                            {{
                              formatCurrency(
                                0,
                                totalParcelsList - tabParcelamento.valorTotal,
                                'decimal'
                              )
                            }}</span
                          >
                        </div>
                      </v-col>
                    </v-row>
                  </v-col>
                  <v-divider vertical></v-divider>
                  <v-col>
                    <v-row dense>
                      <v-data-table
                        :headers="headerTabParcelamentos"
                        :items="tabParcelamento.parcelas"
                        :items-per-page="5"
                        no-data-text="Sem registros para exibir"
                        dense
                      >
                        <template v-slot:item.valor="props">
                          <v-edit-dialog
                            :return-value.sync="props.item.valor"
                            @save="modifyParcels(props.item)"
                            dark
                          >
                            <div>
                              {{ formatDecimalValues(props.item.valor) }}
                            </div>
                            <template
                              v-slot:input
                              v-if="enablePaymentConditionField"
                            >
                              <c-input-money
                                v-model="props.item.valor"
                                dark
                                dense
                                filled
                                defaultClass="text-caption"
                                v-bind:valueWhenIsEmpty="'0'"
                                v-bind:options="{
                                  locale: 'pt-BR',
                                  prefix: '',
                                  suffix: '',
                                  length: 11,
                                  precision: getPrecision(),
                                }"
                              />
                            </template>
                          </v-edit-dialog>
                        </template>
                        <template v-slot:item.ct="props">
                          <div v-if="!enablePaymentConditionField">
                            {{ props.item.ct }}
                          </div>
                          <v-select
                            v-else
                            v-model="props.item.ct"
                            :items="parcelCTsList"
                            class="text-caption"
                            dense
                            dark
                            clearable
                          />
                        </template>
                      </v-data-table>
                    </v-row>
                  </v-col>
                </v-row>
              </v-form>
            </v-container>
          </v-card-text>
          <form-buttons @goBack="show = false" @submitForm="validateForm()" />
        </v-card>
        <payment-select
          ref="paymentConditionSelect"
          v-model="openPaymentSelectDialog"
          @object-pin="objectPin($event)"
        />
      </template>
    </megabit-base-dialog>
    <confirm ref="confirm" v-model="confirm"></confirm>
  </div>
</template>

<script>
import Confirm from '@/components/shared/base-dialog/Confirm.vue'
import MegabitBaseDialog from '@/components/shared/base-dialog/BaseDialog'
import cSelect from '@/components/shared/base-input/BaseSelect.vue'
import cInputMoney from '@/components/shared/base-input/BaseInputMoney.vue'
import FormButtons from '@/components/shared/base-buttons/FormButtons'
import PaymentSelect from '@/components/object/payment-condition/Select'

import { tableMixin } from '@/mixins/component/table/TableMixin'
import { currencyMixin } from '@/mixins/object/currency/CurrencyMixin'
import { parcelMixin } from '@/mixins/object/parcels/ParcelMixin'

import { mapActions, mapState, mapGetters } from 'vuex'

export default {
  name: 'FormRegisterParcel',
  mixins: [tableMixin, currencyMixin, parcelMixin],
  components: {
    cSelect,
    MegabitBaseDialog,
    cInputMoney,
    Confirm,
    FormButtons,
    PaymentSelect,
  },
  data: () => ({
    timer: null,
    confirm: false,
    isLoading: true,
    planoContaList: [],
    tipoVendaList: [],
    openPaymentSelectDialog: false,
  }),

  props: {
    visible: {
      type: Boolean,
      default: true,
    },
    isPDV: {
      type: Boolean,
      default: false,
    },
    tabParcelamento: {
      type: Object,
      default: () => ({
        prefix: '',
        condicao: '',
        tipoVenda: undefined,
        ct: undefined,
        parcela_fases: [],
        valorTotal: 0.0,
        valor: 0.0,
        parcelas: [],
      }),
    },
    tabParcelamentoValid: {
      type: Boolean,
      default: false,
    },
    addParcelamento: {
      type: Function,
    },
  },
  computed: {
    ...mapState('paymentConditionsModule', ['defaultCTsList']),
    ...mapGetters({
      paymentConditionList: 'paymentConditionsModule/getComboBoxList',
      accountList: 'accountsModule/getComboBoxAccountList',
    }),

    headerTabParcelamentos() {
      const header = [
        { text: 'Seq', value: 'index', align: 'start', sortable: false },
        { text: 'Valor', value: 'valor', sortable: false },
        { text: 'Vencimento', value: 'vencimento', sortable: false },
        { text: 'Conta', value: 'conta.CODIGO', sortable: false },
        { text: 'C/T', value: 'ct', sortable: false },
      ]

      return !this.enableAccountField
        ? header.filter((item) => item.text !== 'Conta')
        : header
    },

    enablePaymentConditionField() {
      let result = true
      if (this.isPDV) {
        const flag = this.flagsList.filter(
          (flag) => flag.DESCRICAO == 'CalculaAcrescimoFinanceiroVendaAPrazo'
        )

        if (flag.length > 0) result = false
      }

      return result
    },
    enableAccountField() {
      /**
       * Em conversa com Deonir sobre a flag: InformaContaNoParcelamentoPdvBalcao
       * Pediu para deixar desabilitado por enquanto, este campo não há necessidade
       * no PDV Balcão
       * É utilizado mais em liberações e movimentos de entrada.
       */

      // const flag = this.flagsList.filter(
      //   (flag) => flag.DESCRICAO == 'InformaContaNoParcelamentoPdvBalcao'
      // )

      // // só não habilita edição quando vem do PDV
      // if (flag && this.isPDV) return true
      // else

      return false
    },

    show: {
      get() {
        return this.visible
      },
      set(value) {
        if (!value) {
          this.$emit('close')
        }
      },
    },

    cmpValidForm: {
      get() {
        return this.tabParcelamentoValid
      },
      set(value) {
        this.$emit('update:tabParcelamentoValid', value)
      },
    },

    parcelCTsList() {
      // premissa Deonir: listar CTs da condição, se não tiver nenhuma, todos CTs
      if (
        this.tabParcelamento.tipoVenda &&
        this.tabParcelamento.tipoVenda.CTS &&
        this.tabParcelamento.tipoVenda.CTS.length > 0
      ) {
        const list =
          typeof this.tabParcelamento.tipoVenda.CTS === 'string'
            ? this.tabParcelamento.tipoVenda.CTS.split(',')
            : this.tabParcelamento.tipoVenda.CTS

        return list.filter((c) => c !== '')
      } else {
        return this.defaultCTsList
      }
    },
    totalParcelsList() {
      const total = this.tabParcelamento.parcelas.reduce((total, parcel) => {
        return (total += parseFloat(parcel.valor))
      }, 0.0)
      return total.toFixed(4) * 1
    },
  },
  methods: {
    ...mapActions('paymentConditionsModule', [
      'getPaymentConditionsAPI',
      'getValidCTs',
    ]),

    ...mapActions('accountsModule', ['getAccounts']),

    openDialog() {
      this.$refs.paymentConditionSelect.open()
    },
    objectPin(value) {
      // subentende que esta na lista ao selecionar
      if (this.paymentConditionList.find((el) => el.CODIGO == value.CODIGO)) {
        this.tipoVendaList = this.paymentConditionList
      }

      this.updateParcelField(value)
    },

    validateForm() {
      if (!this.$refs.formParcelamento.validate()) return

      const { valorTotal } = this.tabParcelamento

      if (valorTotal != this.totalParcelsList) {
        this.$toasted.error(
          `Revise os valores das parcelas. Soma difere do total: ${this.formatCurrency(
            0,
            valorTotal,
            'decimal'
          )} / Parcelas: ${this.formatCurrency(
            0,
            this.totalParcelsList,
            'decimal'
          )}`
        )
        return
      }

      this.cmpValue = true

      this.addParcelamento()
    },

    getPrecision() {
      let isGuarani =
        this.getCurrencies && this.getCurrencies[0].cifrao.toLowerCase() == 'g$'
      return !isGuarani ? 2 : ''
    },

    formatDecimalValues(value) {
      const formatted = this.formatCurrency(0, value)
      return formatted.replace(/[^0-9,.]/g, '')
    },

    async updateParcelField(value) {
      if (value) {
        if (
          !this.tabParcelamento.tipoVenda ||
          value.CODIGO !== this.tabParcelamento.tipoVenda.CODIGO
        ) {
          await this.$nextTick()
          this.tabParcelamento.tipoVenda = value
        }
        this.tabParcelamento.condicao = value.NOME

        // vai recuperar o primeiro CT da condição
        if (!value.CTS) {
          this.tabParcelamento.ct = await this.getCTCondicaoPgto(
            this.parcelCTsList
          )
        } else {
          this.tabParcelamento.ct = await this.getCTCondicaoPgto(
            this.tabParcelamento.tipoVenda.CTS
          )
        }

        this.generateParcels()
      } else {
        this.tabParcelamento.condicao = undefined
        this.tabParcelamento.ct = undefined
        this.tabParcelamento.parcelas = []
      }
    },

    updateCTsOnParcels(value) {
      this.tabParcelamento.parcelas.map((item) => {
        item.ct = value == 'Nenhum' ? null : value
      })
    },

    updateAccountOnParcel(value) {
      if (!value && this.tabParcelamento.parcelas.length <= 0) return

      let parcel = this.tabParcelamento.parcelas.filter(
        (p) => p.vencimento == 'A VISTA'
      )[0]
      this.tabParcelamento.parcelas[parcel.index - 1].conta = { ...value }
    },

    modifyParcels(prop) {
      const { valorTotal, parcelas } = this.tabParcelamento

      if (!prop) return

      if (prop.valor > valorTotal) {
        this.$toasted.error('Valor informado supera o Valor Total.')
        return
      }

      const precision = this.getPrecision()

      // total das parcelas que não podem ser alteradas
      let totalParcels = parcelas
        .filter(
          (parcel) =>
            parcel.index == prop.index ||
            parcel.vencimento == 'A VISTA' ||
            parcel.informouValor == 'S'
        )
        .reduce((total, parcel) => {
          return (total += parseFloat(parcel.valor))
        }, 0)

      totalParcels = parseFloat(totalParcels.toFixed(precision))

      let parcelasFiltro = parcelas.filter(
        (parcel) =>
          parcel.index !== prop.index &&
          parcel.vencimento !== 'A VISTA' &&
          parcel.informouValor !== 'S'
      )
      const numParcels = parcelasFiltro.length

      const diff = parseFloat((valorTotal - totalParcels).toFixed(precision))
      let divisao = diff / numParcels
      divisao = parseFloat(divisao.toFixed(precision))

      // caso tiver apenas uma parcela para mudar, faz aqui e sai da função
      if (numParcels == 1) {
        parcelas[parcelasFiltro[0].index - 1].valor = divisao
        return
      }

      let total = 0
      let diferenca = 0

      let nova = parcelas

      for (let i = 1; i <= nova.length; i++) {
        let idx = i - 1
        let parcel = 0
        // se for a parcela que alterou, entao usa o valor dela
        if (
          nova[idx].index == prop.index ||
          nova[idx].vencimento == 'A VISTA' ||
          parcel.informouValor == 'S'
        ) {
          parcel = parseFloat(nova[idx].valor)
        } else {
          parcel = divisao
        }
        parcel = parseFloat(parcel.toFixed(precision))
        nova[idx].valor = parcel
        total += parcel

        if (prop.index == i) prop.informouValor = 'S'

        diferenca = parseFloat(valorTotal) - parseFloat(total)
      }
      // sempre atribuir a diferença na primeira parcela
      nova[0].valor += diferenca
    },

    generateParcels() {
      if (!this.tabParcelamento.tipoVenda) return

      if (this.tabParcelamento.condicao.trim().length > 0) {
        let {
          condicao,
          tipoVenda,
          valorTotal,
          conta,
          ct,
          parcela_fases,
        } = this.tabParcelamento

        const precision = this.getPrecision()

        let aux = condicao
          .replace(/ /g, ',')
          .replace(/[^0-9,.]/g, '')
          .split(',')

        this.tabParcelamento.parcelas = []

        aux = aux.filter((item) => item.trim().length > 0)
        let numParcels = aux.length

        let divisao = valorTotal / numParcels
        divisao = parseFloat(divisao.toFixed(precision))
        let total = 0
        let diferenca = 0

        for (let i = 1; i <= numParcels; i++) {
          let data = new Date()
          let dataVencimento = new Date()
          let incDate = dataVencimento.getDate() + parseInt(aux[i - 1])
          dataVencimento.setDate(incDate)
          dataVencimento =
            parseInt(aux[i - 1]) == 0
              ? (dataVencimento = 'A VISTA')
              : dataVencimento.toLocaleDateString('pt-BR')

          let parcel = divisao
          total += parcel

          this.tabParcelamento.parcelas.push({
            index: this.tabParcelamento.parcelas.length + 1,
            data: data.toLocaleDateString('pt-BR'),
            vencimento: dataVencimento,
            codigoCondicao: tipoVenda.CODIGO,
            condicao: condicao,
            conta: dataVencimento == 'A VISTA' ? conta : undefined,
            ct: ct,
            valor: parcel,
            parcela_fases: parcela_fases,
            informouValor: undefined,
          })

          diferenca = parseFloat(valorTotal) - parseFloat(total)
        }
        this.tabParcelamento.parcelas[0].valor += diferenca
      }
    },

    searchParcels($event, property) {
      try {
        clearTimeout(this.timer)
        this.timer = setTimeout(() => {
          let param = $event.target.value ?? null

          if (!param || param.trim().length <= 0) return

          param = param
            .trim()
            .split('-')[0]
            .trim()

          let payload = { page: 1, hibrido: param }

          let fnc
          let list

          if (property == 'tipoVenda') {
            fnc = this.getPaymentConditionsAPI
            list = 'paymentConditionList'
          } else if (property == 'planoConta') {
            fnc = this.getAccounts
            list = 'accountList'
          }

          if (
            !this.tabParcelamento[property] ||
            parseInt(param) !== this.tabParcelamento[property].CODIGO
          ) {
            this.getDados(payload, property, fnc, list)
          }
        }, 600)
      } catch (error) {
        console.log('erro searchParcel', error)
      }
    },
    async getDados(payload, property, fnc, ownerList) {
      try {
        await fnc(payload)
        this[`${property}List`] = this[ownerList]
      } catch (error) {
        this.$toasted.error(error, { duration: null })
      }
    },
    async setData() {
      // incluir na lista tipoVendaList quando existe no objeto (vindo do PDV)
      const { tipoVenda, ct } = this.tabParcelamento

      if (tipoVenda) {
        this.tipoVendaList.push({
          ...tipoVenda,
          DESCRICAO: tipoVenda.CODIGO + ' - ' + tipoVenda.NOME,
        })

        // se não tiver ct, trazer dos cts padrão o primeiro ct
        if (!ct) {
          this.tabParcelamento.ct = await this.getCTCondicaoPgto(
            this.parcelCTsList
          )
        }
      }
      this.generateParcels()
    },
    async fetch() {
      try {
        // sempre trazer pois pode haver alterações
        await this.getValidCTs()
      } catch (error) {
        this.$toasted.error(error, { duration: null })
      }
    },
  },

  async mounted() {
    await this.fetch()
    await this.setData()
    this.isLoading = !this.isLoading
  },
}
</script>
